import { useRef, useState } from 'react';
import { Upload, Trash2 } from 'lucide-react';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

/**
 * Campo video che supporta sia URL esterni sia upload da file locale.
 * Il file viene inviato al backend (/api/media) che lo salva nel DB
 * e restituisce un URL di servizio. Così il payload di /api/settings
 * rimane leggero.
 */
export default function VideoUploadField({
  value,
  onChange,
  placeholder = 'https://... oppure carica un file .mp4 (H.264 consigliato)',
  maxSizeMB = 60,
}) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('video/')) {
      toast.error('Seleziona un file video valido (mp4 H.264 consigliato)');
      return;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`Video troppo grande. Massimo ${maxSizeMB} MB`);
      return;
    }
    // Avvisa se non è MP4 (compatibilità mobile ridotta)
    if (file.type !== 'video/mp4') {
      toast.warning('Per la massima compatibilità usa MP4 con codec H.264.');
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/media`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000, // 2 min per file grandi
      });
      // Salviamo l'URL relativo, il frontend aggiungerà REACT_APP_BACKEND_URL a runtime
      // Questo previene problemi di Mixed Content se admin salva da localhost
      onChange(res.data.url);
      toast.success('Video caricato con successo!');
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Errore upload: ${detail}`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!value) return;
    // Prova a cancellare il media dal DB se è un URL locale
    if (value.includes('/api/media/')) {
      const id = value.split('/api/media/')[1];
      try { await axios.delete(`${API}/media/${id}`); } catch { /* ignora */ }
    }
    onChange('');
  };

  const isVideo = value && (value.includes('/api/media/') || /\.(mp4|webm|mov|avi|ogg)(\?|$)/i.test(value));

  return (
    <div>
      <div className="flex gap-2">
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="rounded-xl flex-1 font-body text-sm"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-1.5 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-body transition-colors flex-shrink-0 border border-gray-200 disabled:opacity-50 whitespace-nowrap"
        >
          <Upload className="w-4 h-4" />
          {uploading ? 'Upload…' : 'Carica'}
        </button>
        {value && (
          <button
            type="button"
            onClick={handleRemove}
            className="flex items-center gap-1 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-sm font-body transition-colors flex-shrink-0 border border-red-200 whitespace-nowrap"
          >
            <Trash2 className="w-4 h-4" />
            Rimuovi
          </button>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="video/mp4,video/webm,video/*"
          className="hidden"
          onChange={handleFile}
        />
      </div>
      {uploading && (
        <p className="text-xs text-gray-400 font-body mt-1.5">
          ⏳ Caricamento in corso, attendere…
        </p>
      )}
      {isVideo && (
        <video
          src={value}
          controls
          muted
          preload="metadata"
          className="mt-2 w-full rounded-lg max-h-48 bg-black"
        />
      )}
      <p className="text-xs text-gray-400 font-body mt-1.5">
        ✅ Formato consigliato: <strong>MP4 · H.264</strong> — compatibile con tutti i dispositivi · Max {maxSizeMB} MB
      </p>
    </div>
  );
}
